"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import styles from "../css/specialized-tests.module.css";
import {
  getConsultationFull,
} from "../../../../api/clinic/consultationApi.js";

export default function SpecializedTests() {
  const navigate = useNavigate();
  const { consultationData, previousStep, nextStep, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const draftKey = "specializedTests";
  
  const defaultFormData = {
    // Urine Test (Dipstick)
    protein: "",
    blood: "",
    ketones: "",
    specificGravity: "",
    urobilinogen: "",
    leucocytes: "",
    ph: "",
    bilirubin: "",
    
    // Additional observations
    observations: ""
  };

  const [formData, setFormData] = useState(() => getStepDraft(draftKey, defaultFormData));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      (name === "specificGravity" || name === "ph") && typeof value === "string" && value.trim().startsWith("-")
        ? ""
        : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: nextValue };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handlePrevious = () => {
    previousStep();
    navigate("/customer/clinic/symptoms-exam");
  };

  const validate = () => {
    const nextErrors = {};

    const parseOptionalNumber = (val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = Number(val);
      if (!Number.isFinite(num)) return NaN;
      return num;
    };

    const sg = parseOptionalNumber(formData.specificGravity);
    if (sg !== null) {
      if (!Number.isFinite(sg)) {
        nextErrors.specificGravity = "Specific Gravity must be a number.";
      } else if (sg < 0) {
        nextErrors.specificGravity = "Specific Gravity cannot be negative.";
      } else if (sg < 1 || sg > 1.05) {
        nextErrors.specificGravity = "Specific Gravity should be between 1.00 and 1.05.";
      }
    }

    const ph = parseOptionalNumber(formData.ph);
    if (ph !== null) {
      if (!Number.isFinite(ph)) {
        nextErrors.ph = "pH must be a number.";
      } else if (ph < 0) {
        nextErrors.ph = "pH cannot be negative.";
      } else if (ph > 14) {
        nextErrors.ph = "pH cannot be greater than 14.";
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      window.alert(Object.values(nextErrors).join("\n"));
      return false;
    }
    return true;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = consultationData?.consultationId;
        if (!id) return;
        const full = await getConsultationFull(id);
        const u = full?.consultation_urine_dipstick;
        if (!u || cancelled) return;

        const existingDraft = getStepDraft(draftKey, null);
        if (existingDraft) return;

        const next = {
          ...defaultFormData,
          protein: u.protein ?? "",
          blood: u.blood ?? "",
          ketones: u.ketones ?? "",
          specificGravity: u.specificgravity ?? "",
          urobilinogen: u.urobilinogen ?? "",
          leucocytes: u.leucocytes ?? "",
          ph: u.ph ?? "",
          bilirubin: u.bilirubin ?? "",
          observations: u.observations ?? "",
        };

        setFormData(next);
        setStepDraft(draftKey, next);
      } catch (e) {
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consultationData?.consultationId]);

  const handleNext = async () => {
    try {
      if (!validate()) return;
      setSaving(true);
      nextStep();
      navigate("/customer/clinic/diagnosis-icd10");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="step"
            currentStep={5}
            totalSteps={10}
            stepTitle="Specialized Tests"
            stepDetails="Step 5 of 10 · Maria van der Merwe · General Consultation"
            onPrevious={handlePrevious}
            onNext={handleNext}
            nextDisabled={saving}
          />

          <div className={styles.formContainer}>
            {/* URINE TEST (DIPSTICK) */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>URINE TEST (DIPSTICK)</h3>
              
              <div className={styles.testGrid}>
                {/* First Row */}
                <div className={styles.testRow}>
                  <div className={styles.testItem}>
                    <label>Protein</label>
                    <select 
                      name="protein"
                      value={formData.protein}
                      onChange={handleInputChange}
                      className={styles.testSelect}
                    >
                      <option value="">Select...</option>
                      <option value="negative">Negative</option>
                      <option value="trace">Trace</option>
                      <option value="+1">+1</option>
                      <option value="+2">+2</option>
                      <option value="+3">+3</option>
                      <option value="+4">+4</option>
                    </select>
                  </div>

                  <div className={styles.testItem}>
                    <label>Blood</label>
                    <select 
                      name="blood"
                      value={formData.blood}
                      onChange={handleInputChange}
                      className={styles.testSelect}
                    >
                      <option value="">Select...</option>
                      <option value="negative">Negative</option>
                      <option value="trace">Trace</option>
                      <option value="+1">+1</option>
                      <option value="+2">+2</option>
                      <option value="+3">+3</option>
                      <option value="+4">+4</option>
                    </select>
                  </div>

                  <div className={styles.testItem}>
                    <label>Ketones</label>
                    <select 
                      name="ketones"
                      value={formData.ketones}
                      onChange={handleInputChange}
                      className={styles.testSelect}
                    >
                      <option value="">Select...</option>
                      <option value="negative">Negative</option>
                      <option value="trace">Trace</option>
                      <option value="+1">+1</option>
                      <option value="+2">+2</option>
                      <option value="+3">+3</option>
                      <option value="+4">+4</option>
                    </select>
                  </div>
                </div>

                {/* Second Row */}
                <div className={styles.testRow}>
                  <div className={styles.testItem}>
                    <label>Specific Gravity</label>
                    <input
                      type="text"
                      name="specificGravity"
                      value={formData.specificGravity}
                      onChange={handleInputChange}
                      placeholder="e.g., 1.030"
                      className={styles.testInput}
                    />
                  </div>

                  <div className={styles.testItem}>
                    <label>Urobilinogen</label>
                    <select 
                      name="urobilinogen"
                      value={formData.urobilinogen}
                      onChange={handleInputChange}
                      className={styles.testSelect}
                    >
                      <option value="">Select...</option>
                      <option value="normal">Normal</option>
                      <option value="+1">+1</option>
                      <option value="+2">+2</option>
                      <option value="+3">+3</option>
                      <option value="+4">+4</option>
                    </select>
                  </div>

                  <div className={styles.testItem}>
                    <label>Leucocytes</label>
                    <select 
                      name="leucocytes"
                      value={formData.leucocytes}
                      onChange={handleInputChange}
                      className={styles.testSelect}
                    >
                      <option value="">Select...</option>
                      <option value="negative">Negative</option>
                      <option value="trace">Trace</option>
                      <option value="+1">+1</option>
                      <option value="+2">+2</option>
                      <option value="+3">+3</option>
                      <option value="+4">+4</option>
                    </select>
                  </div>
                </div>

                {/* Third Row */}
                <div className={styles.testRow}>
                  <div className={styles.testItem}>
                    <label>pH</label>
                    <input
                      type="text"
                      name="ph"
                      value={formData.ph}
                      onChange={handleInputChange}
                      placeholder="e.g., 6.0"
                      className={styles.testInput}
                    />
                  </div>

                  <div className={styles.testItem}>
                    <label>Bilirubin</label>
                    <select 
                      name="bilirubin"
                      value={formData.bilirubin}
                      onChange={handleInputChange}
                      className={styles.testSelect}
                    >
                      <option value="">Select...</option>
                      <option value="negative">Negative</option>
                      <option value="+1">+1</option>
                      <option value="+2">+2</option>
                      <option value="+3">+3</option>
                      <option value="+4">+4</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Observations */}
              <div className={styles.formGroup}>
                <label>Additional Observations</label>
                <textarea 
                  name="observations"
                  value={formData.observations}
                  onChange={handleInputChange}
                  placeholder="Enter any additional observations about the test results..."
                  className={styles.fullWidthTextarea}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
